import { useState } from 'react';
import { Explain } from '@/components/Explain';
import { OutcomeChip } from '@/components/OutcomeChip';
import { CopyId, Money } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Outcome } from '@/lib/bet';
import { formatDuration } from '@/lib/format';
import type { BetSide } from '../lib/side';
import { compareFacets, differingFacets, type Facet } from '../lib/facets';

/**
 * The attribute diff — every bet-level fact for A and B side by side, with the
 * cells that disagree flagged. This is the heart of Compare: put a disputed bet
 * next to a known-good one of the same game and the structural difference is the
 * highlighted row.
 */
export function CompareSummary({ a, b }: { a: BetSide; b: BetSide }) {
  const facets = compareFacets(
    a.summary,
    b.summary,
    a.bundle.rtpVariation.data?.name != null ? String(a.bundle.rtpVariation.data.name) : null,
    b.bundle.rtpVariation.data?.name != null ? String(b.bundle.rtpVariation.data.name) : null,
  );
  const diffs = differingFacets(facets);
  const [onlyDiff, setOnlyDiff] = useState(false);
  const rows = onlyDiff ? facets.filter((x) => !x.same && !x.context) : facets;

  const sameGame = a.summary.gameUuid === b.summary.gameUuid;

  return (
    <section className="border-b border-hairline">
      <Header
        diffCount={diffs.length}
        onlyDiff={onlyDiff}
        onToggle={() => setOnlyDiff((v) => !v)}
        hasDiff={diffs.length > 0}
      />

      <GameCallout sameGame={sameGame} a={a} b={b} />

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="border-b border-hairline">
              <th className="w-[150px] px-4 py-2 text-left eyebrow">attribute</th>
              <ColHead letter="A" side={a} />
              <ColHead letter="B" side={b} />
            </tr>
          </thead>
          <tbody>
            {rows.map((facet) => (
              <Row key={facet.key} facet={facet} a={a} b={b} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Header({
  diffCount,
  onlyDiff,
  onToggle,
  hasDiff,
}: {
  diffCount: number;
  onlyDiff: boolean;
  onToggle: () => void;
  hasDiff: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
        Attributes
      </span>
      <span
        className={cn(
          'font-mono text-[11px]',
          hasDiff ? 'text-warn' : 'text-ok',
        )}
      >
        {hasDiff ? `${diffCount} difference${diffCount === 1 ? '' : 's'}` : 'no differences'}
      </span>
      {hasDiff && (
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'ml-auto border px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider',
            onlyDiff
              ? 'border-warn/40 bg-warn-bg text-warn'
              : 'border-hairline text-text-dim hover:border-hairline-strong',
          )}
        >
          differences only
        </button>
      )}
    </div>
  );
}

function GameCallout({ sameGame, a, b }: { sameGame: boolean; a: BetSide; b: BetSide }) {
  if (sameGame) {
    return (
      <div className="flex items-center gap-2 border-t border-hairline bg-surface px-4 py-1.5 text-[12px] text-text-dim">
        <span className="text-ok">●</span>
        Same game
        <span className="font-sans text-text">{a.summary.gameName ?? a.summary.gameUuid}</span>
        <span className="text-text-faint">— differences below are bet-specific.</span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-hairline bg-warn-bg/40 px-4 py-1.5 text-[12px] text-text-dim">
      <span className="text-warn">▲</span>
      Two different games
      <span className="font-sans text-text">{a.summary.gameName ?? '—'}</span>
      <span className="text-text-faint">vs</span>
      <span className="font-sans text-text">{b.summary.gameName ?? '—'}</span>
      <span className="text-text-faint">— structural differences are expected.</span>
    </div>
  );
}

function ColHead({ letter, side }: { letter: 'A' | 'B'; side: BetSide }) {
  return (
    <th className="px-4 py-2 text-left align-bottom">
      <div className="flex items-center gap-1.5">
        <LaneLetter letter={letter} />
        <span className="font-mono text-[12px] text-text">{side.summary.betId}</span>
      </div>
    </th>
  );
}

export function LaneLetter({ letter }: { letter: 'A' | 'B' }) {
  return (
    <span className="inline-flex h-4 w-4 items-center justify-center border border-hairline-strong font-condensed text-[10px] font-semibold text-text-dim">
      {letter}
    </span>
  );
}

function Row({ facet, a, b }: { facet: Facet; a: BetSide; b: BetSide }) {
  const flagged = !facet.same && !facet.context;
  return (
    <tr className={cn('border-b border-hairline/60', flagged && 'bg-warn-bg/25')}>
      <td className="px-4 py-1.5 align-top">
        <span className="text-[12px] text-text-dim">
          {facet.term ? <Explain term={facet.term}>{facet.label}</Explain> : facet.label}
        </span>
      </td>
      <Cell facet={facet} value={facet.a} side={a} flagged={flagged} />
      <Cell facet={facet} value={facet.b} side={b} flagged={flagged} />
    </tr>
  );
}

function Cell({
  facet,
  value,
  side,
  flagged,
}: {
  facet: Facet;
  value: string | number | null;
  side: BetSide;
  flagged: boolean;
}) {
  return (
    <td className={cn('px-4 py-1.5 align-top', flagged && 'text-warn')}>
      <FacetValue facet={facet} value={value} side={side} flagged={flagged} />
    </td>
  );
}

function FacetValue({
  facet,
  value,
  side,
  flagged,
}: {
  facet: Facet;
  value: string | number | null;
  side: BetSide;
  flagged: boolean;
}) {
  if (value === null || value === '') return <span className="text-text-faint">—</span>;

  switch (facet.kind) {
    case 'money':
      return (
        <Money
          amount={value}
          meta={side.bundle.currencyMetadata.data}
          currency={side.summary.currency}
          className={flagged ? 'text-warn' : undefined}
        />
      );
    case 'outcome':
      return <OutcomeChip outcome={value as Outcome} />;
    case 'duration':
      return (
        <span className={cn('tnum font-mono text-[12px]', flagged ? 'text-warn' : 'text-text-dim')}>
          {formatDuration(Number(value))}
        </span>
      );
    case 'num':
      return (
        <span className={cn('tnum font-mono text-[12px]', flagged ? 'text-warn' : 'text-text')}>
          {value}
        </span>
      );
    case 'id':
      return <CopyId value={value} truncate={String(value).length > 20} className="text-[12px]" />;
    default:
      return <span className={cn('text-[12px]', flagged ? 'text-warn' : 'text-text')}>{value}</span>;
  }
}
